import { socket } from 'socket'
import { AnyAction, createAsyncThunk, ThunkDispatch } from '@reduxjs/toolkit'
import { Country } from 'react-phone-number-input'

import callAPI from 'api'
import { setSuccess } from 'api/remoteData'
import { queryClient } from 'modules/App/QueryProvider'
import { AppState, UserState, UserBranch, ErrorState } from 'types/store'
import { User as UserType } from 'types/global'
import { Auth, Lead, Lookup, Reminder, Settings, User, Notification } from 'store'
import { Fail, Success } from 'store/types'
import { QUOTATION_APPROVAL_LEVEL_FALLBACK } from 'utils/constants'
import { clearUseLocalStorage } from 'hooks/use-local-storage'

import {
  LdapLoginPayload,
  LoginPayload,
  LoginResponse,
  VerifyUserPayload,
  VerifyUserResponse,
  ResendVerificationPayload,
  ResendVerificationResponse,
  DuplicateValidationPayload,
  DuplicateValidationResponse,
  Branch as BranchResponse,
  LoginData,
} from './types/auth'
import { LEAD_DETAILS_VERSION_KEY } from 'modules/Leads/constant'

export const parseUser: (response: Omit<LoginData, 'branch' | 'auth_token'>) => Partial<UserType> = ({
  pdpa,
  email,
  mobile,
  ...user
}) => ({
  quotationApprovalLevel:
    user.quotation_approval_level?.map((i) => ({
      approvalLevel: i.approval_level || QUOTATION_APPROVAL_LEVEL_FALLBACK,
      branchId: i.branch_id,
    })) ?? [],
  pdpa,
  email,
  firstName: user.fname,
  fullName: user.fullname,
  id: user.user_id,
  lastName: user.lname,
  mobile,
  photoUrl: user.photo_url,
  teams: user.user_groups,
  userProfileId: user.user_profile_id,
  userProfileName: user.user_profile_name,
  userPermissions: user.user_permissions.map((i) => ({
    branchId: i.branch_id,
    permissions: i.permissions,
  })),
  sip: user.sip,
  dialerUrl: user.dialer_url,
  createTs: user.create_ts,
  isSystemUser: user.is_system_user,
  hasAcceptedPDPA: user.has_accepted_pdpa,
  userPreferences: {
    language: user.user_preferences.language,
    updatedTs: user.user_preferences.update_ts,
    leadDetailsVersionPreference: user.user_preferences.lead_details_page_version,
  },
  supervisor: user.supervisor,
  master: {
    id: user.master.id,
    name: user.master.name,
    shortName: user.master.short_name,
    countryCode: user.master.country_code as Country,
    currencyISO: user.master.currency_iso,
    currencySymbol: user.master.currency_symbol,
    logo: {
      logoUrl: user.master.logo_url,
      withBorder: user.master.with_border_logo,
    },
  },
})

export const parseBranches: (response: Array<BranchResponse>) => UserState['userBranches'] = (response) =>
  response.map((b: BranchResponse) => {
    const branch: UserBranch = {
      id: b.branch_id,
      branchId: b.branch_id,
      userBranchId: b.user_branch_id,
      branchName: b.branch_name,
      countryCode: b.branch_country_code,
      tax: b.tax,
    }
    return branch
  })

/**
 * Async function for Login
 * Saves token, user and master data into store
 */

interface FailResponse {
  status: 'FAIL'
  error: ErrorState['error']
}

interface SuccessResponse {
  status: 'SUCCESS'
  redirectTo?: string
  token?: string
  data?: LoginResponse
}

type Response = FailResponse | SuccessResponse | undefined

type LoginThunkApi = {
  getState: () => unknown
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>
}

/**
 * Shared by every login method: they all answer with the same CRM session (or 2FA challenge)
 */
const handleLoginResponse = (
  response: Fail | Success<LoginResponse>,
  email: string | undefined,
  { getState, dispatch }: LoginThunkApi,
): Response => {
  const {
    user: { user },
  } = getState() as AppState

  if (response.status === 'FAIL') {
    const { status, error } = response
    return { status, error }
  }

  if (response.status === 'SUCCESS') {
    const { status, data } = response

    const { auth_2fa } = data

    if (auth_2fa) {
      dispatch(
        User.setUser({
          user: {
            email,
            twoFactors: {
              type: auth_2fa.type,
              sendTo: auth_2fa.send_to,
              timeout: auth_2fa.timeout,
            },
          },
        }),
      )
      return { status, redirectTo: '/auth/2fa' }
    }

    const {
      data: { auth_token, branch, ...rawUser },
    } = data

    const isReloggingIn = user?.id === rawUser.user_id

    const userBranches = branch ? parseBranches(branch) : []

    if (!isReloggingIn) {
      clearLogout(dispatch)
      dispatch(
        User.setUser({
          user: {
            ...parseUser(rawUser),
          },
          userBranches,
        }),
      )
      localStorage.setItem(LEAD_DETAILS_VERSION_KEY, rawUser.user_preferences.lead_details_page_version)
      const initialBranch = userBranches[0]
      const permissions = rawUser.user_permissions.find((i) => i.branch_id === initialBranch.branchId)?.permissions
      rawUser?.user_permissions && dispatch(User.setUserPermissions(permissions))
    }

    const redirectTo = isReloggingIn ? undefined : '/welcome'

    return { status, token: auth_token, data, redirectTo }
  }
}

export const login = createAsyncThunk<Response, LoginPayload>('auth/login', async (payload, thunkApi) =>
  handleLoginResponse(
    await callAPI<LoginResponse, LoginPayload>('crm_auth_login', [payload]),
    payload.email,
    thunkApi,
  ),
)

/**
 * Login with Cartrack LDAP credentials; the username is the user's CRM email
 */
export const ldapLogin = createAsyncThunk<Response, LdapLoginPayload>('auth/ldapLogin', async (payload, thunkApi) =>
  handleLoginResponse(
    await callAPI<LoginResponse, LdapLoginPayload>('crm_auth_ldap_login', [payload]),
    payload.username,
    thunkApi,
  ),
)

/**
 * Async function for Logout
 * Clears store information
 */
export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  await callAPI('crm_auth_logout', [], { requireAuthorization: true })
  clearLogout(dispatch)
})

export const clearLogout = (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) => {
  localStorage.removeItem('token')
  sessionStorage.clear()
  clearUseLocalStorage()
  dispatch(Auth.clear())
  dispatch(Lead.clearSelected())
  dispatch(Lookup.clear())
  dispatch(Notification.clear())
  dispatch(Reminder.clear())
  dispatch(Settings.clear())
  dispatch(User.clear())
  socket.disconnect()

  //Clear all react query cache
  queryClient.removeQueries()
  queryClient.clear()
}

/**
 * Async function to resend verification
 */
export const resendVerification = async (payload: ResendVerificationPayload) =>
  await callAPI<ResendVerificationResponse, ResendVerificationPayload>('crm_resend_user_verification', [payload], {
    requireAuthorization: true,
  })

/*
 * Async function to verify user
 * Return verification results
 */
export const verifyUser = async (payload: VerifyUserPayload) =>
  await callAPI<VerifyUserResponse, VerifyUserPayload>('crm_verify_user', [payload])

export const verifyEmail = async ({ masterId, email }: { masterId: string; email: string }) => {
  const mappedPayload = {
    master_id: masterId,
    email,
  }

  const response = await callAPI<DuplicateValidationResponse, DuplicateValidationPayload>(
    'crm_validate_duplicate_user',
    [mappedPayload],
    { requireAuthorization: true },
  )

  if (response.status === 'FAIL') {
    return response
  }

  const newResponse = { isDuplicate: response.data.is_duplicate }
  return setSuccess(newResponse)
}

export default {
  login,
  logout,
}
